import { Injectable } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import {
  Firestore,
  collection,
  collectionData,
  doc,
  docData,
  addDoc,
  deleteDoc,
  updateDoc,
  query,
  getDoc,
  where,
  getDocs,
  setDoc
} from '@angular/fire/firestore';
import { Client } from 'src/app/interfaces/client';
import { pairwise, takeUntil } from 'rxjs/operators';
import { DataService } from '../../device/local/data.service';
import { Logger } from '../../logs/logger.service';
import { AuthService } from '../../authentication/auth.service';

@Injectable({
  providedIn: 'root'
})
export class ClientService {
  logout$: Subject<boolean> = new Subject<boolean>();
  private subscriptions = new Subscription();

  constructor(
    private firestore: Firestore,
    private auth: AuthService,
    private localData: DataService,
    private logger: Logger
  ) {
    this.setupAuthListener();
  }

  private setupAuthListener(): void {
    // currentUser is a BehaviorSubject(null) that emits immediately on subscribe,
    // so only a logged-in -> logged-out transition may trigger logout$
    const authSub = this.auth.currentUser
      .pipe(pairwise())
      .subscribe(([previousUser, currentUser]) => {
        if (previousUser && !currentUser) {
          this.logout$.next(true);
        }
      });
    this.subscriptions.add(authSub);
  }

  getClients(): Observable<Client[]> {
    const notesRef = query(collection(this.firestore, 'clients'), where('isActiveClient', '!=', false));
    return collectionData(notesRef, { idField: 'id' }).pipe(takeUntil(this.logout$)) as Observable<Client[]>;
  }

  // getClients2(): Observable<newClient[]> {
  //   const notesRef1 = query(
  //     collection(this.firestore, 'pcsClients'),
  //     orderBy('lastName')
  //   );
  //   const notesRef = query(
  //     collection(this.firestore, 'pcsClients'),
  //     where('isActiveClient', '!=', false)
  //   );
  //   const notesRef3 = query(
  //     collection(this.firestore, 'pcsClients')
  //   );
  //   return collectionData(notesRef3, { idField: 'id' }).pipe(
  //     takeUntil(this.logout$)
  //   ) as Observable<newClient[]>;
  // }

  getClientsChat(): Observable<Client[]> {
    const notesRef = query(collection(this.firestore, 'users'), where('role', '==', 'CLIENT'));
    return collectionData(notesRef, { idField: 'id' }).pipe(takeUntil(this.logout$)) as Observable<Client[]>;
  }

  getInactiveClients(): Observable<Client[]> {
    const notesRef = query(collection(this.firestore, 'clients'), where('isActiveClient', '!=', true));
    return collectionData(notesRef, { idField: 'id' }).pipe(takeUntil(this.logout$)) as Observable<Client[]>;
  }

  getClientById(id: string): Observable<Client> {
    const noteDocRef = doc(this.firestore, `clients/${id}`);
    return docData(noteDocRef, { idField: 'id' }).pipe(takeUntil(this.logout$)) as Observable<Client>;
  }

  async getClientLocation(id: string) {
    const ref = doc(this.firestore, `clients/${id}`);

    const docSnap = await getDoc(ref);

    if (docSnap.exists()) {
      const result = docSnap.data();
      return result?.location?.lake ?? null;
    } else {
      console.info('getClientLocation : No location found');
      return null;
    }
  }

  async getImgUrl(id: string) {
    // console.log('getImg client id', id);
    const ref = doc(this.firestore, `clients/${id}`);
    const docSnap = await getDoc(ref);

    if (docSnap.exists()) {
      const result = docSnap.data();
      return result?.boatInfo?.photoUrl ?? null;
    } else {
      console.info('getClientLocation : No location found');
      return null;
    }
  }

  // addClient(client: Client) {
  //   const notesRef = collection(this.firestore, 'clients');
  //   return addDoc(notesRef, client);
  // }
  addClient(client: Client) {
    const notesRef = collection(this.firestore, 'clients');
    return addDoc(notesRef, client);
  }

  async addNewClient(id: string, client: Client) {
    console.log('what is Id?', id);
    const notesRef = doc(this.firestore, 'clients', id);
    console.log('adding doc');
    await setDoc(notesRef, client);
  }

  deleteClient(client: Client) {
    const noteDocRef = doc(this.firestore, `clients/${client.id}`);
    return deleteDoc(noteDocRef);
  }
  updateClient(client: Client) {
    console.log('trying to update', client);
    const noteDocRef = doc(this.firestore, `clients/${client.id}`);
    return updateDoc(noteDocRef, {
      vip: client.vip ?? false,
      isActiveClient: client.isActiveClient,
      isTestClient: client.isTestClient,
      doesClientHaveMobileApp: client.doesClientHaveMobileApp ?? false,
      serviceLevel: client.serviceLevel,
      firstName: client.firstName,
      lastName: client.lastName,
      fullName: client.fullName,
      email: client.email,
      boatInfo: {
        name: client.boatInfo.name,
        make: client.boatInfo.make,
        model: client.boatInfo.model,
        description: client.boatInfo.description,
        isWakeboardBoat: client.boatInfo.isWakeboardBoat,
        numberOfEngines: client.boatInfo.numberOfEngines,
        photoUrl: client.boatInfo.photoUrl
      },
      lastInspection: {
        reportId: client.lastInspection.reportId,
        reportUrl: client.lastInspection.reportUrl,
        inspectionDate: client.lastInspection.inspectionDate
      },
      location: {
        lake: client.location.lake,
        marina: client.location.marina,
        slip: client.location.slip
      }
    });
  }

  updateClientStatus(id: string, status: boolean) {
    const ref = doc(this.firestore, `clients/${id}`);
    return updateDoc(ref, { isActiveClient: status });
  }

  // updateTestClient(id: string) {
  //   const status = false;
  //   const ref = doc(this.firestore, `clients/${id}`);
  //   return updateDoc(ref, { isActiveClient: status });
  // }

  updateClientEmail(id: string, email: string) {
    const ref = doc(this.firestore, `clients/${id}`);
    return updateDoc(ref, { email: email });
  }

  updateClientMobileAppStatus(id: string, status: boolean) {
    const ref = doc(this.firestore, `clients/${id}`);
    return updateDoc(ref, { doesClientHaveMobileApp: status });
  }

  private async getClientId(email: string, firestore: any) {
    const q = query(collection(firestore, 'clients'), where('email', '==', email));

    const querySnapshot = await getDocs(q);
    let clientId: string | undefined;
    querySnapshot.forEach(doc => {
      clientId = doc.id;
    });
    return clientId;
  }

  public async getClientIdByBoatName(boat: string) {
    const q = query(collection(this.firestore, 'clients'), where('boatInfo.name', '==', boat));

    const querySnapshot = await getDocs(q);
    let clientId;
    querySnapshot.forEach(doc => {
      const data = doc.data();
      clientId = { id: doc.id, docData: data };
      return clientId;
    });
    return clientId;
  }

  async whatIsClientId(): Promise<string | undefined> {
    const email = this.localData.userLoggedIn.email;
    const clientId = await this.getClientId(email, this.firestore);
    this.localData.userLoggedIn.clientId = clientId;
    return clientId;
  }

  async getClientBoatById(id: string) {
    console.log('getClientBoatById what is id being passed', id);
    const docRef = doc(this.firestore, 'clients', `${id}`);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.boatInfo.name;
    } else {
      console.log(' getClientBoatById No such document!');
    }
  }

  async getClientBoatInfo(id: string) {
    console.log('getClientBoatInfo what is id being passed', id);
    const docRef = doc(this.firestore, 'clients', `${id}`);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      return data;
    } else {
      console.log(' getClientBoatInfo No such document!');
    }
  }

  async getEmailList() {
    const q = query(collection(this.firestore, 'clients'), where('email', '!=', ''));

    const querySnapshot = await getDocs(q);
    const results = [];
    querySnapshot.forEach(doc => {
      // doc.data() is never undefined for query doc snapshots
      //  console.log('email list', doc.id, ' => ', doc.data());
      const data = doc.data();
      const finalData = {
        id: doc.id,
        firstName: data.firstName,
        lastName: data.lastName,
        fullName: data.fullName,
        email: data.email
      };
      results.push(finalData);
    });
    return results;
  }

  async isClientActive(id: string) {
    const docRef = doc(this.firestore, 'clients', `${id}`);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      if (data.isActiveClient) {
        return true;
      } else if (!data.isActiveClient) {
        return false;
      } else {
        return null;
      }
    } else {
      console.log(' isClientActive No such document!');
    }
  }

  async addMissingVipFieldToClients() {
    const clientCollectionRef = collection(this.firestore, 'clients');
    const snapshot = await getDocs(clientCollectionRef);

    const updates: Promise<void>[] = [];

    snapshot.forEach(docSnap => {
      const data = docSnap.data();
      if (!('vip' in data)) {
        const ref = doc(this.firestore, `clients/${docSnap.id}`);
        updates.push(updateDoc(ref, { vip: false }));
      }
    });

    await Promise.all(updates);
    console.log('VIP field added to all clients missing it.');
  }
}
